import PageWrapper from 'common/components/PageWrapper'
import { fNumber } from 'common/utils/formatNumber'
import { INITIAL_PAGE_SIZE, useHistoryProductById, useProductById } from 'modules/inventory/api/queries'
import { History } from 'modules/inventory/api/type'
import ActionPill from 'modules/inventory/components/ActionPill'
import { formatThaiShortBEWithTime } from 'modules/inventory/helper/format_date'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'


import {
  Avatar,
  Box,
  Card,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'

import NoData from 'components/NoData'
import useInfiniteScrollTable from 'common/components/BasicTable/useInfiniteScrollTable'

const ProductHistory = () => {
  const { t } = useTranslation('product')
  const theme = useTheme()
  const { id } = useParams<{ id: string }>()
  const { data: product } = useProductById(id)
  const [pageSize, setPageSize] = React.useState(INITIAL_PAGE_SIZE)
  const { data, isLoading, hasNextPage, fetchNextPage } = useHistoryProductById(id, { pageSize })

  const onLoadMoreRows = () => {
    if (hasNextPage) {
      fetchNextPage()
    }
  }

  const handleScroll = useInfiniteScrollTable({ isLoading: isLoading, onLoading: onLoadMoreRows })

  const LINKS = [
    {
      name: t('title.product'),
      href: '/dashboard/product',
    },
    {
      name: t('title.history'),
    },
  ]

  const productHistory = data?.pages.flatMap((p) => p.productHistory) ?? [];

  return (
    <PageWrapper title={t('title.history')} titleHead={t('title.history')} links={LINKS}>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          width: '100%',
          height: '100vh',
          typography: 'body1',
          userSelect: 'none',
          bgcolor: 'common.white',
          borderRadius: '24px',
          p: 5,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ color: theme.palette.primary.dark, fontWeight: 600, mb: 1 }}
        >
          {t('title.history')} {product?.name ? `- ${product.name}` : ''}
        </Typography>
        {isLoading ? (
          <Box></Box>
          
        ) : (
          <>
            {productHistory && productHistory.length > 0 ? (
              <TableContainer
                component={Paper}
                onScroll={handleScroll}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                <Table
                  stickyHeader
                  aria-label="customized table"
                  sx={{
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                    '& thead th': {
                      fontSize: '16px',
                      color: theme.palette.common.black,
                      backgroundColor: '#F0F0F0',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                    '& tbody td': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                    '& thead th:first-of-type': { borderTopLeftRadius: 12 },
                    '& thead th:last-of-type': { borderTopRightRadius: 12 },
                    '& tbody tr:last-of-type td:first-of-type': { borderBottomLeftRadius: 12 },
                    '& tbody tr:last-of-type td:last-of-type': { borderBottomRightRadius: 12 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {t('product.history.createdAt')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {t('product.history.transactionDate')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {t('product.history.lot')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {t('product.history.createdBy')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {t('product.history.warehouse')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('product.history.type')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {t('product.history.used')} {`(${productHistory[0]?.unit})`}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {t('product.history.remain')} {`(${productHistory[0]?.unit})`}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(productHistory ?? []).map((row: History, idx: number) => (
                      <TableRow key={row.id || idx} hover>
                        <TableCell>{formatThaiShortBEWithTime(row.created_at)}</TableCell>
                        <TableCell>{row.transaction_date ? formatThaiShortBEWithTime(row.transaction_date) : '-'}</TableCell>
                        <TableCell>{row.lot_number ?? '-'}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                              src={undefined}
                              alt={row.created_by.name}
                              sx={{ width: 28, height: 28 }}
                            />
                            <Typography>{row.created_by.name || '-'}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{row.warehouse.name}</TableCell>
                        <TableCell>
                          <ActionPill action={row.transaction_type} />
                        </TableCell>
                        <TableCell>{
                          row.transaction_type == 'use_product' || row.transaction_type == 'product_issue' || row.transaction_type == 'transfer_warehouse' ?
                            <Typography sx={{ fontSize: '0.875rem' }}>
                              {fNumber(row.used_qty) ?? 0}
                            </Typography>
                            : '-'
                        }</TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              color: row.remaining_qty < 0 ? theme.palette.error.main : 'unset',
                            }}
                          >
                            {row.remaining_qty ? fNumber(row.remaining_qty) : "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <NoData />
            )}
          </>
        )}
      </Card>
    </PageWrapper>
  )
}

export default ProductHistory
